import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { todayStr, daysAgoStr } from '../utils/date';

const PAGE_SIZE = 50;

const ACTION_LABELS = {
  login: '로그인', login_fail: '로그인 실패', logout: '로그아웃',
  password_change: '비밀번호 변경',
  create: '생성', update: '수정', delete: '삭제', restore: '복원',
  publish: '발행', next_version: '후속버전', share: '공유', unshare: '공유 해제', cancel_approval: '승인 취소',
  approve: '승인', reject: '반려', cancel: '취소',
  delete_bulk: '일괄 삭제', purge_bulk: '즉시삭제 일괄',
  hide: '숨김', unhide: '숨김 해제',
  activate: '활성화', import: '가져오기',
  backup: '백업', backup_failure: '백업 실패',
  move: '이동', clone: '복제', update_margin: '공헌이익율 변경',
  create_bulk: '일괄 생성',
};
const RESOURCE_LABELS = {
  auth: '인증', user: '사용자', group: '그룹', organization: '조직',
  estimate: '견적서', approval_request: '승인 요청', share_request: '공유 요청',
  priceset: '원가기준표', product: '상품', part: '자재', customer: '고객',
  bundle: '묶음', service_package: '서비스 패키지', survey_job: '직무·등급',
  setting: '시스템 설정', backup: '백업',
};

export default function AuditLogsPage() {
  const { user } = useAuth();
  const isSysAdmin = user?.uid === 'U-0000';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState({ actions: [], resource_types: [] });

  // 필터
  const [from, setFrom] = useState(daysAgoStr(7));
  const [to, setTo] = useState(todayStr());
  const [actor, setActor] = useState('');
  const [actionFilter, setActionFilter] = useState([]);
  const [resourceFilter, setResourceFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');

  // 상세 모달
  const [detail, setDetail] = useState(null);

  // 정리 모달
  const [showPurge, setShowPurge] = useState(false);
  const [purgeBefore, setPurgeBefore] = useState(daysAgoStr(365));
  const [purgePassword, setPurgePassword] = useState('');
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (actor) params.set('actor', actor);
      if (actionFilter.length) params.set('action', actionFilter.join(','));
      if (resourceFilter.length) params.set('resource_type', resourceFilter.join(','));
      if (statusFilter) params.set('status', statusFilter);
      if (q) params.set('q', q);
      params.set('limit', PAGE_SIZE);
      params.set('offset', page * PAGE_SIZE);
      const r = await api.get(`/audit-logs?${params}`);
      setRows(r.rows || []); setTotal(r.total || 0);
    } catch (e) { console.error(e); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [from, to, actor, actionFilter, resourceFilter, statusFilter, q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/audit-logs/types').then(setTypes).catch(()=>{}); }, []);

  const toggleListItem = (list, item) => list.includes(item) ? list.filter(x=>x!==item) : [...list, item];

  const openDetail = async (id) => {
    try { const r = await api.get(`/audit-logs/${id}`); setDetail(r); }
    catch (e) { alert(e.message); }
  };

  const submitPurge = async () => {
    if (!purgePassword) { alert('비밀번호를 입력하세요.'); return; }
    setPurging(true);
    try {
      const r = await api.del('/audit-logs', { before: purgeBefore, password: purgePassword });
      alert(r.message);
      setShowPurge(false); setPurgePassword('');
      load();
    } catch (e) { alert(e.message); }
    finally { setPurging(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const fmtTime = (s) => s ? new Date(s).toLocaleString('ko-KR', { year:'2-digit', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit' }) : '-';

  return (
    <div>
      <div className="card" style={{marginBottom:14}}>
        <div className="card-header" style={{marginBottom:10}}>
          <div className="card-title">필터</div>
          {isSysAdmin && <button className="btn btn-outline btn-sm" onClick={()=>setShowPurge(true)}>🗑 수동 정리</button>}
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label" style={{fontSize:11}}>시작</label>
            <input className="form-input" type="date" value={from} onChange={e=>{setPage(0);setFrom(e.target.value);}} />
          </div>
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label" style={{fontSize:11}}>종료</label>
            <input className="form-input" type="date" value={to} onChange={e=>{setPage(0);setTo(e.target.value);}} />
          </div>
          <div className="form-group" style={{marginBottom:0,flex:'1 1 180px',minWidth:180}}>
            <label className="form-label" style={{fontSize:11}}>사용자 (이름/이메일/UID)</label>
            <input className="form-input" value={actor} onChange={e=>setActor(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'){setPage(0);load();}}} />
          </div>
          <div className="form-group" style={{marginBottom:0,flex:'2 1 240px',minWidth:200}}>
            <label className="form-label" style={{fontSize:11}}>자유 검색 (리소스 라벨·경로·ID)</label>
            <input className="form-input" value={q} onChange={e=>setQ(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'){setPage(0);load();}}} />
          </div>
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label" style={{fontSize:11}}>상태</label>
            <select className="form-input" value={statusFilter} onChange={e=>{setPage(0);setStatusFilter(e.target.value);}}>
              <option value="">전체</option>
              <option value="success">성공</option>
              <option value="failure">실패</option>
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={()=>{setPage(0);load();}}>🔍 조회</button>
        </div>
        <div style={{marginTop:10,display:'flex',gap:14,flexWrap:'wrap'}}>
          <div>
            <div style={{fontSize:11,color:'#64748b',marginBottom:4}}>리소스 ({resourceFilter.length || '전체'})</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:4,maxWidth:520}}>
              {types.resource_types.map(rt => {
                const on = resourceFilter.includes(rt);
                return <button key={rt} type="button" onClick={()=>{setPage(0);setResourceFilter(toggleListItem(resourceFilter,rt));}}
                  style={{padding:'2px 8px',fontSize:11,borderRadius:10,cursor:'pointer',
                    background:on?'#3b82f6':'#f1f5f9',color:on?'#fff':'#475569',border:`1px solid ${on?'#3b82f6':'#e2e8f0'}`}}>
                  {RESOURCE_LABELS[rt]||rt}
                </button>;
              })}
              {types.resource_types.length===0 && <span style={{fontSize:11,color:'#cbd5e1'}}>로그 없음</span>}
            </div>
          </div>
          <div>
            <div style={{fontSize:11,color:'#64748b',marginBottom:4}}>액션 ({actionFilter.length || '전체'})</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:4,maxWidth:520}}>
              {types.actions.map(a => {
                const on = actionFilter.includes(a);
                return <button key={a} type="button" onClick={()=>{setPage(0);setActionFilter(toggleListItem(actionFilter,a));}}
                  style={{padding:'2px 8px',fontSize:11,borderRadius:10,cursor:'pointer',
                    background:on?'#10b981':'#f1f5f9',color:on?'#fff':'#475569',border:`1px solid ${on?'#10b981':'#e2e8f0'}`}}>
                  {ACTION_LABELS[a]||a}
                </button>;
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div style={{padding:'8px 14px',borderBottom:'1px solid #e2e8f0',fontSize:12,color:'#64748b',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>총 {total.toLocaleString()}건</span>
          <span>페이지 {page+1} / {totalPages}</span>
        </div>
        {loading ? <div style={{padding:40,textAlign:'center',color:'#94a3b8'}}>로딩 중...</div> : (
          <div style={{overflowX:'auto'}}>
            <table style={{fontSize:12}}>
              <thead><tr>
                <th style={{width:140}}>시간</th>
                <th style={{width:100}}>사용자</th>
                <th style={{width:80}}>액션</th>
                <th style={{width:90}}>리소스</th>
                <th>대상</th>
                <th>변경 필드</th>
                <th style={{width:60,textAlign:'center'}}>상태</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="clickable" style={{cursor:'pointer'}} onClick={()=>openDetail(r.id)}>
                    <td style={{whiteSpace:'nowrap'}}>{fmtTime(r.created_at)}</td>
                    <td>{r.actor_name || '-'} <span style={{fontSize:10,color:'#94a3b8'}}>{r.actor_uid||''}</span></td>
                    <td>{ACTION_LABELS[r.action] || r.action}</td>
                    <td>{RESOURCE_LABELS[r.resource_type] || r.resource_type}{r.resource_id ? <span style={{fontSize:10,color:'#94a3b8'}}> #{r.resource_id}</span>:''}</td>
                    <td style={{maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.resource_label || '-'}</td>
                    <td style={{fontSize:10,color:'#475569'}}>{(r.changed_fields||[]).join(', ') || '-'}</td>
                    <td style={{textAlign:'center'}}>
                      <span className={`badge ${r.status==='success'?'badge-green':'badge-red'}`}>{r.status==='success'?'성공':'실패'}</span>
                    </td>
                  </tr>
                ))}
                {rows.length===0 && <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>로그가 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div style={{padding:'8px 14px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'center',gap:6,alignItems:'center'}}>
          <button className="btn btn-outline btn-sm" disabled={page===0} onClick={()=>setPage(p=>Math.max(0,p-1))}>◀ 이전</button>
          <span style={{fontSize:12,color:'#64748b',margin:'0 10px'}}>{page+1} / {totalPages}</span>
          <button className="btn btn-outline btn-sm" disabled={page>=totalPages-1} onClick={()=>setPage(p=>p+1)}>다음 ▶</button>
        </div>
      </div>

      {/* 상세 모달 */}
      {detail && (
        <div className="modal-overlay" onClick={()=>setDetail(null)}>
          <div className="modal" style={{width:900,maxHeight:'90vh',overflowY:'auto'}} onClick={ev=>ev.stopPropagation()}>
            <div className="modal-header">
              <h3>감사 로그 상세 #{detail.id}</h3>
              <button className="modal-close" onClick={()=>setDetail(null)}>✕</button>
            </div>
            <div className="modal-body">
              <table style={{fontSize:12,marginBottom:14}}>
                <tbody>
                  <tr><td style={{color:'#64748b',width:120}}>시간</td><td>{fmtTime(detail.created_at)}</td></tr>
                  <tr><td style={{color:'#64748b'}}>사용자</td><td>{detail.actor_name} ({detail.actor_uid}) · {detail.actor_email||'-'} · {detail.actor_org_uid||'-'}</td></tr>
                  <tr><td style={{color:'#64748b'}}>액션</td><td>{ACTION_LABELS[detail.action]||detail.action}</td></tr>
                  <tr><td style={{color:'#64748b'}}>리소스</td><td>{RESOURCE_LABELS[detail.resource_type]||detail.resource_type} #{detail.resource_id||'-'} {detail.resource_label?`— ${detail.resource_label}`:''}</td></tr>
                  <tr><td style={{color:'#64748b'}}>요청</td><td style={{fontFamily:'monospace',fontSize:11}}>{detail.request_method} {detail.request_path}</td></tr>
                  <tr><td style={{color:'#64748b'}}>IP / UA</td><td style={{fontSize:11}}>{detail.ip || '-'} <span style={{color:'#94a3b8'}}>{detail.user_agent||''}</span></td></tr>
                  <tr><td style={{color:'#64748b'}}>변경 필드</td><td>{(detail.changed_fields||[]).join(', ') || '-'}</td></tr>
                  <tr><td style={{color:'#64748b'}}>상태</td><td>{detail.status} {detail.error_message && <span style={{color:'#dc2626'}}>— {detail.error_message}</span>}</td></tr>
                </tbody>
              </table>
              <div className="grid-2" style={{gap:14}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'#64748b',marginBottom:6}}>Before</div>
                  <pre style={{background:'#fef2f2',padding:10,borderRadius:8,border:'1px solid #fecaca',fontSize:11,maxHeight:400,overflow:'auto'}}>{detail.before_json ? JSON.stringify(detail.before_json,null,2) : '(없음)'}</pre>
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'#64748b',marginBottom:6}}>After</div>
                  <pre style={{background:'#ecfdf5',padding:10,borderRadius:8,border:'1px solid #a7f3d0',fontSize:11,maxHeight:400,overflow:'auto'}}>{detail.after_json ? JSON.stringify(detail.after_json,null,2) : '(없음)'}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 수동 정리 모달 */}
      {showPurge && (
        <div className="modal-overlay" onClick={()=>setShowPurge(false)}>
          <div className="modal" style={{width:420}} onClick={ev=>ev.stopPropagation()}>
            <div className="modal-header">
              <h3>감사 로그 수동 정리</h3>
              <button className="modal-close" onClick={()=>setShowPurge(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger" style={{marginBottom:14,fontSize:12}}>
                지정 일자 이전 모든 감사 로그를 영구 삭제합니다. 복구할 수 없습니다.
              </div>
              <div className="form-group">
                <label className="form-label">기준 일자 (이 날짜 이전 삭제)</label>
                <input className="form-input" type="date" value={purgeBefore} onChange={e=>setPurgeBefore(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">비밀번호 확인</label>
                <input className="form-input" type="password" value={purgePassword} onChange={e=>setPurgePassword(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')submitPurge();}} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setShowPurge(false)}>취소</button>
              <button className="btn" style={{background:'#dc2626',color:'#fff'}} disabled={purging} onClick={submitPurge}>{purging?'삭제 중...':'삭제'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
